import { X } from "@phosphor-icons/react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { useToast } from "@/hooks/useToast"
import { cn } from "@/lib/utils"

const viewportVariants = cva("fixed z-100 flex max-h-screen w-auto flex-col", {
  variants: {
    position: {
      top: "top-4 left-[50%] translate-x-[-50%]",
      bottom: "bottom-4 left-[50%] translate-x-[-50%]",
      "top-right": "top-4 right-7",
      "top-left": "top-4 left-7",
      "bottom-right": "right-7 bottom-4",
      "bottom-left": "bottom-4 left-7",
    },
  },
  defaultVariants: {
    position: "bottom",
  },
})

export const ToastProvider = ToastPrimitives.Provider

type ToastViewportProps = React.ComponentPropsWithRef<typeof ToastPrimitives.Viewport> & {
  position?: VariantProps<typeof viewportVariants>["position"]
}
export const ToastViewport = ({ className, position, ...props }: ToastViewportProps) => (
  <ToastPrimitives.Viewport className={cn(viewportVariants({ position }), className)} {...props} />
)

const toastVariants = cva(
  "bg-surface-inverted text-on-primary data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 flex h-10 w-full items-center gap-2 rounded-xl pr-3.25 pl-4 text-sm leading-5 transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--radix-toast-swipe-end-x) data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x) data-[swipe=move]:transition-none",
  {
    variants: {
      position: {
        top: "data-[state=closed]:slide-out-to-top-full data-[state=open]:slide-in-from-top-full mt-3",
        bottom: "data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-bottom-full mb-3",
        "top-right": "data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full mt-3",
        "top-left": "data-[state=closed]:slide-out-to-left-full data-[state=open]:slide-in-from-left-full mt-3",
        "bottom-right": "data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full mb-3",
        "bottom-left": "data-[state=closed]:slide-out-to-left-full data-[state=open]:slide-in-from-left-full mb-3",
      },
    },
    defaultVariants: {
      position: "bottom",
    },
  },
)

export const Toast = ({
  className,
  position,
  ...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>) => {
  return <ToastPrimitives.Root className={cn(toastVariants({ position }), className)} {...props} />
}

export const ToastClose = ({ className, ...props }: React.ComponentPropsWithRef<typeof ToastPrimitives.Close>) => (
  <ToastPrimitives.Close className={className} {...props}>
    <X className="m-2 h-6 w-6" />
  </ToastPrimitives.Close>
)

export const ToastDescription = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ToastPrimitives.Description>) => (
  <ToastPrimitives.Description className={className} {...props} />
)

export type ToastProps = React.ComponentPropsWithRef<typeof Toast> & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  isClosable?: boolean
  "data-testid"?: string
}

export const CustomToast = ({ id, isClosable, description, duration, position, children, ...props }: ToastProps) => {
  return (
    <div key={id}>
      <Toast key={id} duration={duration} position={position} {...props}>
        <ToastDescription data-testid="toast-description" className={!isClosable ? "mr-0.75 py-2.5" : ""}>
          {description}
        </ToastDescription>
        {children}
        {isClosable && <ToastClose className="-mr-1 pl-2" />}
      </Toast>
      <ToastViewport position={position} />
    </div>
  )
}

export const Toaster = () => {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map((toast: ToastProps) => {
        return <CustomToast key={toast.id} {...toast} />
      })}
    </ToastProvider>
  )
}
